import type { SliceMetric } from '@death-star/shared';

/**
 * Shared formatting + metric-classification helpers for the slicing dashboard.
 *
 * These were previously hand-rolled (and subtly divergent) in four separate
 * components — SliceTile, SliceBarChart, SliceTrendChart, and MetricHeader.
 * The divergence caused the "25127%" bug: MetricHeader's inline time-metric
 * check omitted `council_wall_clock`, so a p95 duration in seconds (~251) was
 * run through the percentage formatter. Centralizing here keeps a single source
 * of truth.
 */

/** Metrics whose values are durations measured in seconds, not 0..1 rates. */
const TIME_METRICS = new Set<string>(['ttm', 'ttfr', 'council_wall_clock']);

/** Metrics that have no baseline comparison (current-only bars). */
const NO_BASELINE_METRICS = new Set<string>(['auto_approval', 'override_rate']);

/**
 * Metrics where a *lower* value is the improvement. Used to color the
 * "vs baseline" delta correctly. auto_approval is the only higher-is-better
 * metric; every other slicing metric (time-to-merge, revert/override
 * rates, council wall-clock) improves as it goes down.
 */
const LOWER_IS_BETTER = new Set<string>([
  'ttm',
  'ttfr',
  'council_wall_clock',
  'revert_rate',
  'override_rate',
]);

export function isTimeMetric(metric: string): boolean {
  return TIME_METRICS.has(metric);
}

export function isNoBaselineMetric(metric: string): boolean {
  return NO_BASELINE_METRICS.has(metric);
}

/**
 * Direction of improvement for a metric.
 * 'lower' = a decrease is good, 'higher' = an increase is good.
 */
export function metricDirection(metric: string): 'lower' | 'higher' {
  return LOWER_IS_BETTER.has(metric) ? 'lower' : 'higher';
}

/** Render a 0..1 rate as a whole-number percentage. */
export const PCT = (v: number) => `${(v * 100).toFixed(0)}%`;

/** Render a duration in seconds as minutes (<= 1h) or hours. */
export const SECS = (v: number) => (Math.abs(v) > 3600 ? `${(v / 3600).toFixed(1)} h` : `${Math.round(v / 60)} m`);

/**
 * Pick the value formatter for a given metric. Time metrics get the
 * minutes/hours duration formatter; everything else is a 0..1 rate rendered
 * as a percentage. An explicit override always wins.
 */
export function pickFormatter(metric: SliceMetric, override?: (v: number) => string): (v: number) => string {
  if (override) return override;
  return isTimeMetric(metric) ? SECS : PCT;
}

/** Format a single metric value using the metric's natural formatter. */
export function formatValue(metric: SliceMetric, value: number): string {
  return pickFormatter(metric)(value);
}
